/**
 * App — root router shell.
 *
 * Wires the QueryClient, BrowserRouter, and AuthProvider, then defines the route
 * table: a public `/login` route (the persona switcher) and protected `/chat`,
 * `/retrieval`, `/audit`, `/agents` routes rendered inside the nav shell
 * (Header + Sidebar).
 */

import type { ReactNode } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Header } from './components/layout/Header';
import { Sidebar } from './components/layout/Sidebar';
import { Login } from './pages/Login';
import { Callback } from './pages/Callback';
import { Chat } from './pages/Chat';
import { Retrieval } from './pages/Retrieval';
import { Knowledge } from './pages/Knowledge';
import { Audit } from './pages/Audit';
import { AllowancesPage } from './pages/Allowances';
import { MyAllowance } from './pages/MyAllowance';
import { Dashboard } from './pages/Dashboard';
import { Evals } from './pages/Evals';
import { Tools } from './pages/Tools';
import { Connectors } from './pages/Connectors';
import { AgentList } from './pages/AgentList';
import { AgentEditor } from './pages/AgentEditor';
import { RunHistory } from './pages/RunHistory';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** Wraps protected routes: redirects to /login when unauthenticated. */
function ProtectedLayout(): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center text-steel-500">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="h-full flex">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function App(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/callback" element={<Callback />} />
            <Route element={<ProtectedLayout />}>
              <Route path="/chat" element={<Chat />} />
              <Route path="/retrieval" element={<Retrieval />} />
              <Route path="/knowledge" element={<Knowledge />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/allowances" element={<AllowancesPage />} />
              <Route path="/my-allowance" element={<MyAllowance />} />
              <Route path="/evals" element={<Evals />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/connectors" element={<Connectors />} />
              <Route path="/agents" element={<AgentList />} />
              <Route path="/agents/runs" element={<RunHistory />} />
              <Route path="/agents/new" element={<AgentEditor />} />
              <Route path="/agents/:agentId" element={<AgentEditor />} />
            </Route>
            <Route path="/" element={<Navigate to="/chat" replace />} />
            <Route path="*" element={<Navigate to="/chat" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
